import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { GlobeCrossedOut, Refresh, RefreshCrossedOut } from "@/assets/icons/ds";
import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { DireProgressBar } from "@/components/Layout/ProgressBar/DireProgressBar";
import { SimCardDetail } from "@/services/model";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import {
  hasWatchSimLocked,
  hasWatchSimPending,
  isWatchSimPausing,
} from "@/utils/deviceUtils";
import { getNextPaymentDate } from "@/utils/tariffUtils";

/**
 * "Watch SIM-Status": the same shape as `SubscriptionStatus` ("Abo-Status"),
 * scoped to the Watch SIM option - whether it is still being prepared,
 * running, or scheduled to stop renewing. Its own dynamic hint lives beside
 * the tab, not inside this card - see `ShowWatchSimOptions`'s `WatchSimHint`.
 */
export const WatchSimStatus = ({
  simDetail,
  action,
}: {
  simDetail: SimCardDetail;
  action?: ReactNode;
}) => {
  const { t, i18n } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.status",
  });
  const { t: tDisplay } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.display",
  });

  // Only PENDING is still being provisioned. ORDERED is the status the
  // backend returns right after checkout — the option is already booked and
  // billed, so it must present as active (pairing help lives in WatchSimHint).
  const isBeingActivated = hasWatchSimPending(simDetail);
  const isPausing = isWatchSimPausing(simDetail);
  // Mirrors SubscriptionStatus's own lock handling - a locked Watch SIM must
  // not present itself as actively renewing.
  const isLocked = hasWatchSimLocked(simDetail);

  // Same billing cycle as the Abo, so it counts down from the same fields.
  const nextBillDate =
    simDetail.remainingDays !== undefined
      ? getLocalizedDateString(
          getNextPaymentDate(simDetail.remainingDays),
          i18n.language,
        )
      : undefined;

  const elapsedPercentage =
    simDetail.minContractDuration && simDetail.remainingDays !== undefined
      ? ((simDetail.minContractDuration - simDetail.remainingDays) /
          simDetail.minContractDuration) *
        100
      : undefined;

  return (
    <SubscriptionCard
      dataTestid="watch-sim-status"
      title={t("title")}
      action={action}
    >
      <div className="flex w-full flex-col gap-4 rounded-md bg-surface-quaternary p-4">
        <div className="flex w-full items-start gap-4">
          <span className="shrink-0 text-foreground-primary">
            {isLocked ? (
              <GlobeCrossedOut className="size-6" />
            ) : isPausing ? (
              <RefreshCrossedOut className="size-6" />
            ) : (
              <Refresh className="size-6" />
            )}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1 text-foreground-primary">
            <p className="truncate-safe text-body-base font-semibold leading-none">
              {isLocked
                ? t("locked")
                : isBeingActivated
                  ? t("activating")
                  : isPausing
                    ? // Unlike Abo-Status, a scheduled pause replaces "Aktiv"
                      // as the heading — "Aktiv" + "Wird pausiert am …" read
                      // as if the pause had not been requested yet.
                      t("pausingTitle")
                    : t("active")}
            </p>
            {!isLocked &&
              (isBeingActivated ? (
                <p className="truncate-safe text-body-xs leading-none">
                  {tDisplay("pending")}
                </p>
              ) : (
                nextBillDate && (
                  <p className="truncate-safe text-body-xs leading-none">
                    <Trans
                      t={t}
                      i18nKey={isPausing ? "pausing" : "nextBill"}
                      components={[<span key="0" className="font-semibold" />]}
                      values={{ date: nextBillDate }}
                    />
                  </p>
                )
              ))}
          </div>
        </div>
        <DireProgressBar
          value={isLocked || isBeingActivated ? undefined : elapsedPercentage}
        />
      </div>
    </SubscriptionCard>
  );
};
